const SW_PATH = '/sw.js';

export function isPushSupported() {
  return (
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator
  );
}

export async function requestNotificationPermission() {
  if (!isPushSupported()) return 'unsupported';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  return Notification.requestPermission();
}

export function getNotificationPermission() {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission;
}

// User preference stored in localStorage — separate from browser permission
export function getPushEnabled() {
  if (typeof window === 'undefined') return true;
  try {
    return localStorage.getItem('agentPushEnabled') !== 'false';
  } catch {
    return true;
  }
}

export function setPushEnabled(enabled) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem('agentPushEnabled', String(enabled));
  } catch {}
}

async function waitForActivation(reg, timeoutMs = 5000) {
  if (reg.active) return reg;
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(reg), timeoutMs);
    const sw = reg.installing || reg.waiting;
    if (!sw) { 
      clearTimeout(timer); 
      resolve(reg); 
      return; 
    }
    
    // Explicitly check for immediate current status changes
    if (sw.state === 'activated') {
      clearTimeout(timer);
      resolve(reg);
      return;
    }

    sw.addEventListener('statechange', function onState() {
      if (sw.state === 'activated' || reg.active) {
        clearTimeout(timer);
        sw.removeEventListener('statechange', onState);
        resolve(reg);
      }
    });
  });
}

async function getActiveSwRegistration() {
  if (!isPushSupported()) return null;
  try {
    // Explicitly pass base origin route scopes to prevent lookup path drift errors
    const existing = await navigator.serviceWorker.getRegistration('/');
    if (existing) {
      if (existing.active) return existing;
      return waitForActivation(existing);
    }
    const reg = await navigator.serviceWorker.register(SW_PATH, { scope: '/' });
    return waitForActivation(reg);
  } catch (err) {
    console.warn('SW registration failed:', err);
    return null;
  }
}

export async function initializePushNotifications() {
  if (!isPushSupported()) return { status: 'unsupported' };

  const permission = await requestNotificationPermission();
  if (permission !== 'granted') {
    return { status: permission === 'denied' ? 'denied' : 'prompt' };
  }

  const reg = await registerServiceWorker();
  if (!reg) {
    return { status: 'sw-not-ready' };
  }

  try {
    await subscribeToPush();
    return { status: 'ready' };
  } catch (err) {
    console.warn('Push initialization failed:', err);
    return { status: 'failed' };
  }
}

export async function showBrowserNotification(title, body, options = {}) {
  if (!isPushSupported() || !getPushEnabled()) return null;

  let permission = Notification.permission;
  if (permission === 'default') {
    permission = await Notification.requestPermission();
  }
  if (permission !== 'granted') return null;

  const notifOptions = {
    vibrate: options.vibrate || [200, 100, 200],
    body,
    icon: '/logo192.png',
    badge: '/logo192.png',
    tag: options.tag ?? `order-${options.orderId ?? Date.now()}`,
    requireInteraction: options.requireInteraction ?? true,
    data: options.data || { url: '/agent/dashboard' },
    actions: options.actions || [
      { action: 'view',    title: 'View Order' },
      { action: 'dismiss', title: 'Dismiss'    },
    ],
  };

  const reg = await getActiveSwRegistration();
  if (reg && reg.active) {
    try {
      await reg.showNotification(title, notifOptions);
      return null;
    } catch (err) {
      console.warn('SW showNotification failed, falling back to native Notification API', err);
    }
  }

  // Fallback to native runtime browser windows if Service Workers fail
  try {
    return new Notification(title, notifOptions);
  } catch (err) {
    console.error('Native Notification API failed:', err);
    return null;
  }
}

export async function registerServiceWorker() {
  return getActiveSwRegistration();
}

export async function getNotificationStatus() {
  if (!isPushSupported()) {
    return {
      working: false,
      reason: 'unsupported',
      message: 'Notifications not supported in this browser',
      actionable: false
    };
  }

  if (Notification.permission === 'denied') {
    return {
      working: false,
      reason: 'denied',
      message: 'Notification permission blocked in browser settings',
      actionable: false
    };
  }

  if (Notification.permission === 'default') {
    return {
      working: false,
      reason: 'default',
      message: 'Notification permission not granted yet',
      actionable: true
    };
  }

  const reg = await getActiveSwRegistration();
  const hasActiveWorker = !!reg?.active;

  return {
    working: hasActiveWorker,
    reason: hasActiveWorker ? null : 'sw-not-active',
    message: hasActiveWorker ? 'Notifications are working' : 'Service worker not active',
    actionable: !hasActiveWorker
  };
}

export async function reRegisterServiceWorker() {
  if (!isPushSupported()) return null;

  try {
    const existing = await navigator.serviceWorker.getRegistration('/');

    if (existing) {
      await existing.update();
      // Even after triggering an update task, always process worker lifecycle checks safely
      return waitForActivation(existing);
    }

    const reg = await navigator.serviceWorker.register(SW_PATH, { scope: '/' });
    return waitForActivation(reg);
  } catch (err) {
    console.warn('📡 SW: Re-registration failed:', err);
    return null;
  }
}

/**
 * Convert a base64-encoded VAPID public key to a Uint8Array
 * for use with PushManager.subscribe().
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

/**
 * Subscribe the current device to push notifications via the backend.
 * Requires the backend to expose:
 *   GET  /api/push/vapid-public-key  → { publicKey: "<VAPID_PUBLIC_KEY>" }
 *   POST /api/push/subscribe         → accepts { endpoint, keys, userAgent }
 */
export async function subscribeToPush() {
  if (!isPushSupported()) return null;

  const reg = await getActiveSwRegistration();
  if (!reg) return null;

  // Check if already subscribed
  const existingSub = await reg.pushManager.getSubscription();
  if (existingSub) return existingSub;

  try {
    // Fetch VAPID public key from backend
    const api = (await import('../api/axios')).default;
    const { data } = await api.get('/push/vapid-public-key');
    if (!data?.publicKey) {
      console.warn('Push: No VAPID public key from backend');
      return null;
    }

    const applicationServerKey = urlBase64ToUint8Array(data.publicKey);

    const subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });

    const subData = subscription.toJSON();

    await api.post('/push/subscribe', {
      endpoint: subData.endpoint,
      keys: subData.keys,
      userAgent: navigator.userAgent,
    });

    console.log('📡 Push: Subscribed successfully');
    return subscription;
  } catch (err) {
    console.warn('📡 Push: Subscription failed:', err);
    return null;
  }
}
